import { useState } from 'react';
import { Copy, Plus, Trash2 } from 'lucide-react';
import {
    WorkflowNode,
    WorkflowNodeConfig,
    TriggerData,
    TransformData,
    HttpRequestData,
    LookupMediaData,
    BranchData,
    EmitCodesData,
    CLICKERBOOTH_ACTIONS,
    KNOWN_STEP_TYPES,
    CatalogTrigger,
} from './types';
import { VariableSuggestion } from './variables';
import ExpressionInput from './ExpressionInput';

interface NodeConfigPanelProps {
    node: WorkflowNode;
    suggestions: VariableSuggestion[];
    triggerOptions: CatalogTrigger[];
    onUpdateMeta: (patch: { key?: string; label?: string }) => void;
    onUpdateConfig: (config: WorkflowNodeConfig) => void;
    onDelete: () => void;
    onDuplicate: () => void;
}

const labelCls = 'block text-xs font-medium text-gray-600 mb-1';
const inputCls =
    'w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none';
const sectionCls = 'space-y-3';

let conditionCounter = 0;
function newConditionId() {
    conditionCounter += 1;
    return `c${Date.now().toString(36)}${conditionCounter}`;
}

export default function NodeConfigPanel({
    node,
    suggestions,
    triggerOptions,
    onUpdateMeta,
    onUpdateConfig,
    onDelete,
    onDuplicate,
}: NodeConfigPanelProps) {
    const isKnown =
        node.type === 'trigger' || KNOWN_STEP_TYPES.includes(node.type);
    return (
        <div className="flex h-full flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                <span className="text-sm font-semibold capitalize text-gray-800">
                    {node.type} step
                </span>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={onDuplicate}
                        className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800"
                    >
                        <Copy size={14} /> Duplicate
                    </button>
                    {node.type !== 'trigger' && (
                        <button
                            type="button"
                            onClick={onDelete}
                            className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                        >
                            <Trash2 size={14} /> Delete
                        </button>
                    )}
                </div>
            </div>

            <div className="mt-3 space-y-4 overflow-auto pr-1">
                <div>
                    <label className={labelCls}>
                        Reference key (used in expressions)
                    </label>
                    <input
                        className={`${inputCls} font-mono`}
                        value={node.key ?? ''}
                        placeholder={node.id}
                        onChange={(e) =>
                            onUpdateMeta({ key: e.target.value })
                        }
                    />
                </div>
                <div>
                    <label className={labelCls}>Label</label>
                    <input
                        className={inputCls}
                        value={node.label ?? ''}
                        placeholder="Display name"
                        onChange={(e) =>
                            onUpdateMeta({ label: e.target.value })
                        }
                    />
                </div>

                {node.type === 'trigger' && (
                    <TriggerForm
                        data={node.data as TriggerData}
                        triggerOptions={triggerOptions}
                        onChange={onUpdateConfig}
                    />
                )}
                {!isKnown && (
                    <GenericConfigForm
                        data={node.data}
                        onChange={onUpdateConfig}
                    />
                )}
                {node.type === 'transform' && (
                    <TransformForm
                        data={node.data as TransformData}
                        suggestions={suggestions}
                        onChange={onUpdateConfig}
                    />
                )}
                {node.type === 'httpRequest' && (
                    <HttpForm
                        data={node.data as HttpRequestData}
                        suggestions={suggestions}
                        onChange={onUpdateConfig}
                    />
                )}
                {node.type === 'lookupMedia' && (
                    <LookupMediaForm
                        data={node.data as LookupMediaData}
                        suggestions={suggestions}
                        onChange={onUpdateConfig}
                    />
                )}
                {node.type === 'branch' && (
                    <BranchForm
                        data={node.data as BranchData}
                        suggestions={suggestions}
                        onChange={onUpdateConfig}
                    />
                )}
                {node.type === 'emitCodes' && (
                    <EmitForm
                        data={node.data as EmitCodesData}
                        suggestions={suggestions}
                        onChange={onUpdateConfig}
                    />
                )}
            </div>
        </div>
    );
}

const TRIGGER_HINTS: Record<string, React.ReactNode> = {
    event: (
        <>
            Runs when the booth emits this workflow's event. Exposes{' '}
            <code>trigger.name</code>, <code>trigger.payload</code>,{' '}
            <code>trigger.eventId</code>, <code>trigger.occurredAt</code>.
        </>
    ),
    share: (
        <>
            Runs when a guest share is created. Exposes{' '}
            <code>trigger.shareIdentifiers</code> (everything shared),{' '}
            <code>trigger.destination</code>, <code>trigger.method</code>{' '}
            (email/text), and <code>trigger.galleryId</code>.
        </>
    ),
    mediaUploaded: (
        <>
            Runs when a photo or video is uploaded. Exposes{' '}
            <code>trigger.url</code>, <code>trigger.mimeType</code>,{' '}
            <code>trigger.fileSize</code>, <code>trigger.width</code>/
            <code>trigger.height</code>, and <code>trigger.shareIdentifier</code>.
        </>
    ),
};

function TriggerForm({
    data,
    triggerOptions,
    onChange,
}: {
    data: TriggerData;
    triggerOptions: CatalogTrigger[];
    onChange: (d: TriggerData) => void;
}) {
    const triggerType = data.triggerType ?? 'qrScan';
    return (
        <div className={sectionCls}>
            <div>
                <label className={labelCls}>Trigger type</label>
                <select
                    className={inputCls}
                    value={triggerType}
                    onChange={(e) =>
                        onChange({ ...data, triggerType: e.target.value })
                    }
                >
                    {(triggerOptions.length > 0
                        ? triggerOptions
                        : [{ type: 'qrScan', label: 'QR scan' }]
                    ).map((t) => (
                        <option key={t.type} value={t.type}>
                            {t.label}
                        </option>
                    ))}
                </select>
            </div>
            {triggerType === 'qrScan' && (
                <>
                    <div>
                        <label className={labelCls}>Scan value name</label>
                        <input
                            className={`${inputCls} font-mono`}
                            value={data.inputName ?? ''}
                            placeholder="raw"
                            onChange={(e) =>
                                onChange({ ...data, inputName: e.target.value })
                            }
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={!!data.parseJson}
                            onChange={(e) =>
                                onChange({
                                    ...data,
                                    parseJson: e.target.checked,
                                })
                            }
                        />
                        Parse scanned string as JSON (exposes <code>data</code>)
                    </label>
                </>
            )}
            {TRIGGER_HINTS[triggerType] && (
                <p className="text-xs leading-relaxed text-gray-500">
                    {TRIGGER_HINTS[triggerType]}
                </p>
            )}
        </div>
    );
}

function TransformForm({
    data,
    suggestions,
    onChange,
}: {
    data: TransformData;
    suggestions: VariableSuggestion[];
    onChange: (d: TransformData) => void;
}) {
    const assignments = data.assignments ?? [];
    const update = (next: typeof assignments) =>
        onChange({ ...data, assignments: next });

    return (
        <div className={sectionCls}>
            <label className={labelCls}>Output values</label>
            {assignments.map((a, i) => (
                <div
                    key={i}
                    className="space-y-1 rounded-md border border-gray-200 p-2"
                >
                    <input
                        className={`${inputCls} font-mono`}
                        value={a.key}
                        placeholder="fieldName"
                        onChange={(e) => {
                            const next = [...assignments];
                            next[i] = { ...a, key: e.target.value };
                            update(next);
                        }}
                    />
                    <ExpressionInput
                        value={a.expression}
                        suggestions={suggestions}
                        onChange={(expression) => {
                            const next = [...assignments];
                            next[i] = { ...a, expression };
                            update(next);
                        }}
                    />
                    <button
                        type="button"
                        className="text-xs text-red-600"
                        onClick={() =>
                            update(assignments.filter((_, j) => j !== i))
                        }
                    >
                        Remove
                    </button>
                </div>
            ))}
            <AddButton
                label="Add value"
                onClick={() =>
                    update([...assignments, { key: '', expression: '' }])
                }
            />
        </div>
    );
}

function HttpForm({
    data,
    suggestions,
    onChange,
}: {
    data: HttpRequestData;
    suggestions: VariableSuggestion[];
    onChange: (d: HttpRequestData) => void;
}) {
    const headers = data.headers ?? [];
    return (
        <div className={sectionCls}>
            <div>
                <label className={labelCls}>Method</label>
                <select
                    className={inputCls}
                    value={data.method ?? 'GET'}
                    onChange={(e) =>
                        onChange({
                            ...data,
                            method: e.target.value as HttpRequestData['method'],
                        })
                    }
                >
                    {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
                        <option key={m} value={m}>
                            {m}
                        </option>
                    ))}
                </select>
            </div>
            <div>
                <label className={labelCls}>
                    URL (JSONata; use quotes for literals)
                </label>
                <ExpressionInput
                    value={data.url ?? ''}
                    suggestions={suggestions}
                    placeholder={'"https://api.example.com/" & trigger.data.id'}
                    onChange={(url) => onChange({ ...data, url })}
                />
            </div>
            <div>
                <label className={labelCls}>Headers</label>
                {headers.map((h, i) => (
                    <div key={i} className="mb-1 flex gap-1">
                        <input
                            className={`${inputCls} w-1/3`}
                            value={h.key}
                            placeholder="Header"
                            onChange={(e) => {
                                const next = [...headers];
                                next[i] = { ...h, key: e.target.value };
                                onChange({ ...data, headers: next });
                            }}
                        />
                        <div className="flex-1">
                            <ExpressionInput
                                value={h.value}
                                suggestions={suggestions}
                                placeholder={'"Bearer xyz"'}
                                onChange={(value) => {
                                    const next = [...headers];
                                    next[i] = { ...h, value };
                                    onChange({ ...data, headers: next });
                                }}
                            />
                        </div>
                        <button
                            type="button"
                            className="text-red-600"
                            onClick={() =>
                                onChange({
                                    ...data,
                                    headers: headers.filter(
                                        (_, j) => j !== i,
                                    ),
                                })
                            }
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                ))}
                <AddButton
                    label="Add header"
                    onClick={() =>
                        onChange({
                            ...data,
                            headers: [...headers, { key: '', value: '' }],
                        })
                    }
                />
            </div>
            <div>
                <label className={labelCls}>Body</label>
                <select
                    className={`${inputCls} mb-1`}
                    value={data.bodyMode ?? 'none'}
                    onChange={(e) =>
                        onChange({
                            ...data,
                            bodyMode: e.target
                                .value as HttpRequestData['bodyMode'],
                        })
                    }
                >
                    <option value="none">None</option>
                    <option value="json">JSON</option>
                    <option value="raw">Raw</option>
                    <option value="multipart">Multipart form-data</option>
                </select>
                {(data.bodyMode === 'json' || data.bodyMode === 'raw') && (
                    <ExpressionInput
                        value={data.body ?? ''}
                        suggestions={suggestions}
                        multiline
                        placeholder={'{ "id": trigger.data.id }'}
                        onChange={(body) => onChange({ ...data, body })}
                    />
                )}
                {data.bodyMode === 'multipart' && (
                    <MultipartPartsEditor
                        data={data}
                        suggestions={suggestions}
                        onChange={onChange}
                    />
                )}
            </div>
        </div>
    );
}

function MultipartPartsEditor({
    data,
    suggestions,
    onChange,
}: {
    data: HttpRequestData;
    suggestions: VariableSuggestion[];
    onChange: (d: HttpRequestData) => void;
}) {
    const parts = data.parts ?? [];
    const update = (next: typeof parts) => onChange({ ...data, parts: next });
    return (
        <div className="space-y-2">
            <p className="text-[11px] text-gray-500">
                File parts download their source URL and attach the bytes — use{' '}
                <code>lookup.media[0].original_uri</code> or{' '}
                <code>trigger.url</code> to forward a photo.
            </p>
            {parts.map((p, i) => (
                <div
                    key={i}
                    className="space-y-1 rounded-md border border-gray-200 p-2"
                >
                    <div className="flex gap-1">
                        <select
                            className={`${inputCls} w-24`}
                            value={p.kind}
                            onChange={(e) => {
                                const next = [...parts];
                                next[i] = {
                                    ...p,
                                    kind: e.target.value as 'field' | 'file',
                                };
                                update(next);
                            }}
                        >
                            <option value="field">Field</option>
                            <option value="file">File</option>
                        </select>
                        <input
                            className={`${inputCls} flex-1 font-mono`}
                            value={p.name}
                            placeholder="field name"
                            onChange={(e) => {
                                const next = [...parts];
                                next[i] = { ...p, name: e.target.value };
                                update(next);
                            }}
                        />
                    </div>
                    <ExpressionInput
                        value={p.value}
                        suggestions={suggestions}
                        placeholder={
                            p.kind === 'file'
                                ? 'lookup.media[0].original_uri'
                                : '"a value" or trigger.destination'
                        }
                        onChange={(value) => {
                            const next = [...parts];
                            next[i] = { ...p, value };
                            update(next);
                        }}
                    />
                    {p.kind === 'file' && (
                        <>
                            <ExpressionInput
                                value={p.filename ?? ''}
                                suggestions={suggestions}
                                placeholder={'filename (optional), e.g. "photo.jpg"'}
                                onChange={(filename) => {
                                    const next = [...parts];
                                    next[i] = { ...p, filename };
                                    update(next);
                                }}
                            />
                            <ExpressionInput
                                value={p.contentType ?? ''}
                                suggestions={suggestions}
                                placeholder={
                                    'content type (optional), e.g. "image/jpeg"'
                                }
                                onChange={(contentType) => {
                                    const next = [...parts];
                                    next[i] = { ...p, contentType };
                                    update(next);
                                }}
                            />
                        </>
                    )}
                    <button
                        type="button"
                        className="text-xs text-red-600"
                        onClick={() => update(parts.filter((_, j) => j !== i))}
                    >
                        Remove part
                    </button>
                </div>
            ))}
            <AddButton
                label="Add part"
                onClick={() =>
                    update([...parts, { name: '', kind: 'field', value: '' }])
                }
            />
        </div>
    );
}

function LookupMediaForm({
    data,
    suggestions,
    onChange,
}: {
    data: LookupMediaData;
    suggestions: VariableSuggestion[];
    onChange: (d: LookupMediaData) => void;
}) {
    return (
        <div className={sectionCls}>
            <div>
                <label className={labelCls}>
                    Share identifier(s) (JSONata)
                </label>
                <ExpressionInput
                    value={data.shareIdentifiers ?? ''}
                    suggestions={suggestions}
                    placeholder={'trigger.shareIdentifiers'}
                    onChange={(shareIdentifiers) =>
                        onChange({ ...data, shareIdentifiers })
                    }
                />
                <p className="mt-1 text-[11px] leading-relaxed text-gray-500">
                    Accepts one identifier, an array, or a comma-joined string —
                    every identifier is looked up. Outputs <code>media</code>{' '}
                    (array) and <code>count</code>.
                </p>
            </div>
        </div>
    );
}

function BranchForm({
    data,
    suggestions,
    onChange,
}: {
    data: BranchData;
    suggestions: VariableSuggestion[];
    onChange: (d: BranchData) => void;
}) {
    const conditions = data.conditions ?? [];
    return (
        <div className={sectionCls}>
            <label className={labelCls}>
                Conditions (first truthy wins; otherwise the "else" path)
            </label>
            {conditions.map((c, i) => (
                <div
                    key={c.id}
                    className="space-y-1 rounded-md border border-gray-200 p-2"
                >
                    <input
                        className={inputCls}
                        value={c.label ?? ''}
                        placeholder="Condition label (edge name)"
                        onChange={(e) => {
                            const next = [...conditions];
                            next[i] = { ...c, label: e.target.value };
                            onChange({ ...data, conditions: next });
                        }}
                    />
                    <ExpressionInput
                        value={c.expression}
                        suggestions={suggestions}
                        placeholder={'trigger.data.tier = "vip"'}
                        onChange={(expression) => {
                            const next = [...conditions];
                            next[i] = { ...c, expression };
                            onChange({ ...data, conditions: next });
                        }}
                    />
                    <button
                        type="button"
                        className="text-xs text-red-600"
                        onClick={() =>
                            onChange({
                                ...data,
                                conditions: conditions.filter(
                                    (_, j) => j !== i,
                                ),
                            })
                        }
                    >
                        Remove
                    </button>
                </div>
            ))}
            <AddButton
                label="Add condition"
                onClick={() =>
                    onChange({
                        ...data,
                        conditions: [
                            ...conditions,
                            { id: newConditionId(), label: '', expression: '' },
                        ],
                    })
                }
            />
        </div>
    );
}

function EmitForm({
    data,
    suggestions,
    onChange,
}: {
    data: EmitCodesData;
    suggestions: VariableSuggestion[];
    onChange: (d: EmitCodesData) => void;
}) {
    const codes = data.codes ?? [];
    return (
        <div className={sectionCls}>
            <label className={labelCls}>Clickerbooth codes to emit</label>
            {codes.map((code, ci) => (
                <div
                    key={ci}
                    className="space-y-2 rounded-md border border-gray-200 p-2"
                >
                    <select
                        className={inputCls}
                        value={code.action}
                        onChange={(e) => {
                            const next = [...codes];
                            next[ci] = { ...code, action: e.target.value };
                            onChange({ ...data, codes: next });
                        }}
                    >
                        <option value="">Select action…</option>
                        {CLICKERBOOTH_ACTIONS.map((a) => (
                            <option key={a} value={a}>
                                {a}
                            </option>
                        ))}
                    </select>
                    {(code.fields ?? []).map((f, fi) => (
                        <div key={fi} className="flex gap-1">
                            <input
                                className={`${inputCls} w-1/3 font-mono`}
                                value={f.key}
                                placeholder="dataKey"
                                onChange={(e) => {
                                    const next = [...codes];
                                    const fields = [...(code.fields ?? [])];
                                    fields[fi] = { ...f, key: e.target.value };
                                    next[ci] = { ...code, fields };
                                    onChange({ ...data, codes: next });
                                }}
                            />
                            <div className="flex-1">
                                <ExpressionInput
                                    value={f.valueExpression}
                                    suggestions={suggestions}
                                    placeholder={'lookup.body.email'}
                                    onChange={(valueExpression) => {
                                        const next = [...codes];
                                        const fields = [...(code.fields ?? [])];
                                        fields[fi] = { ...f, valueExpression };
                                        next[ci] = { ...code, fields };
                                        onChange({ ...data, codes: next });
                                    }}
                                />
                            </div>
                            <button
                                type="button"
                                className="text-red-600"
                                onClick={() => {
                                    const next = [...codes];
                                    next[ci] = {
                                        ...code,
                                        fields: (code.fields ?? []).filter(
                                            (_, j) => j !== fi,
                                        ),
                                    };
                                    onChange({ ...data, codes: next });
                                }}
                            >
                                <Trash2 size={14} />
                            </button>
                        </div>
                    ))}
                    <AddButton
                        label="Add field"
                        onClick={() => {
                            const next = [...codes];
                            next[ci] = {
                                ...code,
                                fields: [
                                    ...(code.fields ?? []),
                                    { key: '', valueExpression: '' },
                                ],
                            };
                            onChange({ ...data, codes: next });
                        }}
                    />
                    <button
                        type="button"
                        className="block text-xs text-red-600"
                        onClick={() =>
                            onChange({
                                ...data,
                                codes: codes.filter((_, j) => j !== ci),
                            })
                        }
                    >
                        Remove code
                    </button>
                </div>
            ))}
            <AddButton
                label="Add code"
                onClick={() =>
                    onChange({
                        ...data,
                        codes: [...codes, { action: '', fields: [] }],
                    })
                }
            />
        </div>
    );
}

// Raw-JSON editor for step types this editor has no dedicated form for. Lets
// newly-registered backend steps be configured without a code change here.
function GenericConfigForm({
    data,
    onChange,
}: {
    data: WorkflowNodeConfig;
    onChange: (d: WorkflowNodeConfig) => void;
}) {
    const [text, setText] = useState(() => JSON.stringify(data, null, 2));
    const [error, setError] = useState<string | null>(null);

    return (
        <div className={sectionCls}>
            <label className={labelCls}>
                Configuration (JSON) — this step type has no custom editor
            </label>
            <textarea
                className={`${inputCls} h-40 font-mono`}
                value={text}
                onChange={(e) => {
                    setText(e.target.value);
                    try {
                        onChange(JSON.parse(e.target.value));
                        setError(null);
                    } catch (err) {
                        setError('Invalid JSON');
                    }
                }}
            />
            {error && <div className="text-xs text-red-600">{error}</div>}
        </div>
    );
}

function AddButton({
    label,
    onClick,
}: {
    label: string;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex items-center gap-1 rounded-md border border-dashed border-gray-300 px-2 py-1 text-xs text-gray-600 hover:border-blue-400 hover:text-blue-600"
        >
            <Plus size={14} /> {label}
        </button>
    );
}
